import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import {
  format, parseISO, isValid, addMonths, subMonths,
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, isToday, isBefore, parse,
} from 'date-fns';
import { useDateLocale } from '../../utils/dateLocale';

interface Props {
  value: string;
  onChange: (v: string) => void;
  withTime?: boolean;
  min?: string;
  /** Month shown when the picker opens with no value yet (e.g. the trip start
   *  date) — does not set a value, only where the calendar lands. */
  defaultMonth?: string;
  required?: boolean;
  className?: string;
  placeholder?: string;
  error?: boolean;
}

const POPUP_W = { date: 288, datetime: 320 };
const VIEWPORT_MARGIN = 8;

function parseDatePart(v: string): Date | null {
  if (!v) return null;
  const d = v.includes('T') ? parseISO(v) : parse(v, 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : null;
}
function parseTimePart(v: string) {
  if (v?.includes('T')) {
    const [, time] = v.split('T');
    const [h = '00', m = '00'] = time.split(':');
    return { h: h.padStart(2, '0'), m: m.padStart(2, '0') };
  }
  return { h: '00', m: '00' };
}

export default function DatePicker({ value, onChange, withTime = false, min, defaultMonth, className = '', placeholder, error }: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const weekdays = eachDayOfInterval({
    start: startOfWeek(new Date(), { weekStartsOn: 1 }),
    end:   endOfWeek(new Date(), { weekStartsOn: 1 }),
  }).map(d => format(d, 'EEEEEE', { locale: dateLocale }));
  const parsed  = parseDatePart(value);
  const minDate = min ? parseDatePart(min) : null;
  const defaultMonthDate = defaultMonth ? parseDatePart(defaultMonth) : null;
  const { h: initH, m: initM } = parseTimePart(value);

  const [open, setOpen]     = useState(false);
  const [cursor, setCursor] = useState<Date>(parsed ?? defaultMonthDate ?? new Date());
  const [hour, setHour]     = useState(initH);
  const [minute, setMinute] = useState(initM);
  const [pos, setPos]       = useState({ top: 0, left: 0, maxHeight: 0 });

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef   = useRef<HTMLDivElement>(null);

  // Sync cursor & time when value changes externally.
  // Compare numerically so a round-trip ("1" → emit "01" → useEffect)
  // doesn't overwrite the user's in-progress raw input.
  useEffect(() => {
    if (parsed) setCursor(parsed);
    if (withTime) {
      const { h, m } = parseTimePart(value);
      setHour(prev => Number(prev) === Number(h) ? prev : h);
      setMinute(prev => Number(prev) === Number(m) ? prev : m);
    }
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        popupRef.current?.contains(e.target as Node)
      ) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Placed from the popup's measured height, not a guess: a fixed estimate put
  // it partly above the viewport when opening upwards (flight legs sit low in
  // the booking modal), hiding the month and the first week. Clamped to the
  // viewport, and scrollable when not even that fits.
  const calcPos = useCallback(() => {
    if (!triggerRef.current || !popupRef.current) return;
    const r     = triggerRef.current.getBoundingClientRect();
    const popH  = popupRef.current.scrollHeight;
    const popW  = withTime ? POPUP_W.datetime : POPUP_W.date;
    const maxHeight  = window.innerHeight - VIEWPORT_MARGIN * 2;
    const spaceBelow = window.innerHeight - r.bottom - VIEWPORT_MARGIN;
    const spaceAbove = r.top - VIEWPORT_MARGIN;
    const openUp = spaceBelow < popH && spaceAbove > spaceBelow;
    const wanted = openUp ? r.top - popH - 6 : r.bottom + 6;
    const top  = Math.max(VIEWPORT_MARGIN, Math.min(wanted, window.innerHeight - Math.min(popH, maxHeight) - VIEWPORT_MARGIN));
    const left = Math.max(VIEWPORT_MARGIN, Math.min(r.left, window.innerWidth - popW - VIEWPORT_MARGIN));
    setPos({ top, left, maxHeight });
  }, [withTime]);

  // Before paint, so the popup never flashes at its unmeasured spot. Re-run when
  // the month changes (5 or 6 week rows) and when the modal behind scrolls.
  useLayoutEffect(() => {
    if (!open) return;
    calcPos();
    window.addEventListener('resize', calcPos);
    window.addEventListener('scroll', calcPos, true);
    return () => {
      window.removeEventListener('resize', calcPos);
      window.removeEventListener('scroll', calcPos, true);
    };
  }, [open, cursor, calcPos]);

  function openPicker() {
    // With no value chosen yet, land on the suggested month (trip start /
    // the previously entered leg) rather than wherever the cursor last was.
    if (!parsed) setCursor(defaultMonthDate ?? new Date());
    setOpen(o => !o);
  }

  function emit(day: Date, h = hour, m = minute) {
    const dateStr = format(day, 'yyyy-MM-dd');
    onChange(withTime ? `${dateStr}T${h.padStart(2, '0')}:${m.padStart(2, '0')}` : dateStr);
  }

  function selectDay(day: Date) {
    if (withTime) { setCursor(day); emit(day); }
    else { emit(day); setOpen(false); }
  }

  function handleHour(raw: string) {
    // Keep raw digits while typing — don't pad or clamp (user may still be typing).
    const cleaned = raw.replace(/\D/g, '').slice(0, 2);
    setHour(cleaned);
    const n = Number(cleaned);
    if (cleaned !== '' && n <= 23 && parsed) emit(parsed, cleaned.padStart(2, '0'), minute);
  }
  function handleMinute(raw: string) {
    const cleaned = raw.replace(/\D/g, '').slice(0, 2);
    setMinute(cleaned);
    const n = Number(cleaned);
    if (cleaned !== '' && n <= 59 && parsed) emit(parsed, hour, cleaned.padStart(2, '0'));
  }
  function commitHour(raw: string) {
    const n = Math.min(23, Math.max(0, Number(raw.replace(/\D/g, '') || '0')));
    const v = String(n).padStart(2, '0');
    setHour(v);
    if (parsed) emit(parsed, v, minute);
  }
  function commitMinute(raw: string) {
    const n = Math.min(59, Math.max(0, Number(raw.replace(/\D/g, '') || '0')));
    const v = String(n).padStart(2, '0');
    setMinute(v);
    if (parsed) emit(parsed, hour, v);
  }

  const monthStart = startOfMonth(cursor);
  const days       = eachDayOfInterval({
    start: startOfWeek(monthStart, { weekStartsOn: 1 }),
    end:   endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }),
  });

  const label = parsed
    ? withTime ? `${format(parsed, 'd MMM yyyy', { locale: dateLocale })}  ${hour}:${minute}` : format(parsed, 'd MMM yyyy', { locale: dateLocale })
    : placeholder ?? (withTime ? t('common.pickDateTime') : t('common.pickDate'));

  const popup = open && (
    <div
      ref={popupRef}
      className="fixed z-[9999] overflow-y-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl p-4 animate-fade-in"
      style={{ top: pos.top, left: pos.left, maxHeight: pos.maxHeight || undefined, width: withTime ? POPUP_W.datetime : POPUP_W.date }}
    >
      {/* Month nav */}
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={() => setCursor(c => subMonths(c, 1))}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" /></svg>
        </button>
        <span className="font-mono text-[11px] font-semibold tracking-widest uppercase text-slate-700 dark:text-slate-200">
          {format(cursor, 'MMMM yyyy', { locale: dateLocale })}
        </span>
        <button type="button" onClick={() => setCursor(c => addMonths(c, 1))}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" /></svg>
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 mb-1">
        {weekdays.map(d => (
          <div key={d} className="text-center font-mono text-[9px] font-semibold tracking-widest uppercase text-slate-400 py-1">{d}</div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-y-0.5">
        {days.map(day => {
          const sel      = parsed ? isSameDay(day, parsed) : false;
          const thisMonth = isSameMonth(day, cursor);
          const today    = isToday(day);
          const disabled = minDate ? isBefore(day, minDate) : false;
          return (
            <button key={day.toISOString()} type="button" disabled={disabled} onClick={() => selectDay(day)}
              className={`h-8 w-full rounded-lg font-mono text-[12px] transition-colors cursor-pointer
                ${sel      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-semibold'
                : today    ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 font-semibold'
                : thisMonth ? 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                :             'text-slate-300 dark:text-slate-600'}
                ${disabled ? 'opacity-30 pointer-events-none' : ''}
              `}>
              {format(day, 'd')}
            </button>
          );
        })}
      </div>

      {/* Time picker */}
      {withTime && (
        <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
          <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">{t('common.time')}</p>
          <div className="flex items-center gap-2">
            <input type="text" inputMode="numeric" maxLength={2} value={hour}
              onChange={e => handleHour(e.target.value)}
              onFocus={e => e.target.select()}
              onBlur={e => commitHour(e.target.value)}
              className="w-14 h-9 text-center font-mono text-lg font-semibold text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:border-teal-400 transition-colors"
            />
            <span className="font-mono text-xl text-slate-400">:</span>
            <input type="text" inputMode="numeric" maxLength={2} value={minute}
              onChange={e => handleMinute(e.target.value)}
              onFocus={e => e.target.select()}
              onBlur={e => commitMinute(e.target.value)}
              className="w-14 h-9 text-center font-mono text-lg font-semibold text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:border-teal-400 transition-colors"
            />
            <button type="button" onClick={() => setOpen(false)}
              className="ml-auto h-9 px-4 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-mono text-[11px] font-semibold cursor-pointer hover:bg-slate-700 dark:hover:bg-slate-100 transition-colors">
              {t('common.done')}
            </button>
          </div>
        </div>
      )}

      {/* Footer date-only */}
      {!withTime && (
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
          {value && (
            <button type="button" onClick={() => { onChange(''); setOpen(false); }}
              className="font-mono text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors cursor-pointer">
              {t('common.clear')}
            </button>
          )}
          <button type="button" onClick={() => selectDay(new Date())}
            className="ml-auto font-mono text-[10px] text-teal-600 dark:text-teal-400 hover:text-teal-700 transition-colors cursor-pointer">
            {t('common.today')}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className={`relative ${className}`}>
      <button ref={triggerRef} type="button" onClick={openPicker}
        className={`input w-full flex items-center justify-between gap-2 text-left${error ? ' input-error' : ''}`}>
        <span className={parsed ? 'text-slate-800 dark:text-slate-200' : 'text-slate-400'}>{label}</span>
        <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
        </svg>
      </button>
      {typeof document !== 'undefined' && createPortal(popup, document.body)}
    </div>
  );
}
